import * as PopoverPrimitive from "@radix-ui/react-popover";
import { Clock } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/core/lib/utils";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, i) =>
  String(i).padStart(2, "0"),
);

/** Divide "HH:MM" em partes válidas, caindo no default quando o valor está incompleto. */
function splitTime(value: string) {
  const [hour = "09", minute = "00"] = value.split(":");
  return {
    hour: HOURS.includes(hour) ? hour : "09",
    minute: MINUTES.includes(minute) ? minute : "00",
  };
}

interface TimeColumnProps {
  options: string[];
  selected: string;
  onSelect: (option: string) => void;
  label: string;
}

function TimeColumn({ options, selected, onSelect, label }: TimeColumnProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);

  // Abre a coluna já com o valor atual visível, sem rolar a página em volta.
  useEffect(() => {
    const list = listRef.current;
    const item = selectedRef.current;
    if (list && item) {
      list.scrollTop = item.offsetTop - list.offsetTop;
    }
  }, []);

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label={label}
      className="flex flex-col gap-1 px-1 overflow-y-auto max-h-56"
    >
      {options.map((option) => {
        const isSelected = option === selected;
        return (
          <button
            key={option}
            ref={isSelected ? selectedRef : undefined}
            type="button"
            role="option"
            aria-selected={isSelected}
            onClick={() => onSelect(option)}
            className={cn(
              "flex justify-center items-center w-14 h-10 text-sm font-bold rounded-xl transition-colors shrink-0",
              isSelected
                ? "text-white bg-darkgray-900"
                : "text-darkgray-700 hover:bg-warmgray-100",
            )}
          >
            {option}
          </button>
        );
      })}
    </div>
  );
}

export interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  name?: string;
  disabled?: boolean;
  "aria-label"?: string;
  /** Rótulos acessíveis das colunas; o form passa já traduzidos. */
  hourLabel: string;
  minuteLabel: string;
}

/**
 * Seletor de hora com popover próprio.
 *
 * Substitui o `<input type="time">` porque o dropdown nativo é chrome do browser: o item
 * selecionado usa o `accent-color` do sistema e não aceita raio de borda, hover ou tipografia
 * do design system. Mantém o mesmo contrato "HH:MM" do input para o React Hook Form.
 */
export function TimePicker({
  value,
  onChange,
  onBlur,
  name,
  disabled,
  "aria-label": ariaLabel,
  hourLabel,
  minuteLabel,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const { hour, minute } = useMemo(() => splitTime(value), [value]);

  return (
    <PopoverPrimitive.Root
      /*
       * `modal` é obrigatório dentro da Sheet: ela é um Dialog do Radix, que marca todo o
       * conteúdo fora dela como inerte (`pointer-events: none`). Um popover portalizado
       * como não-modal cai nessa região e deixa de responder aos cliques — no chat, sem
       * Dialog em volta, o mesmo componente funcionava.
       */
      modal
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onBlur?.();
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          name={name}
          disabled={disabled}
          aria-label={ariaLabel}
          className={cn(
            "flex justify-between items-center px-3 py-2 w-full h-10 text-sm bg-white rounded-md border duration-300 border-secondary-200 hover:border-secondary-300 focus:border-secondary-300 focus-visible:outline-none",
            disabled &&
              "cursor-not-allowed bg-secondary border-secondary-200 text-neutral-500",
          )}
        >
          <span>{`${hour}:${minute}`}</span>
          <Clock className="w-4 h-4 text-warmgray-400" aria-hidden />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          /* Acima do chat flutuante (z-[999999]), senão o popover abre atrás dele. */
          className="z-[1000001] p-2 bg-white rounded-2xl border shadow-lg border-warmgray-200"
        >
          <div className="flex gap-1">
            <TimeColumn
              options={HOURS}
              selected={hour}
              label={hourLabel}
              onSelect={(next) => onChange(`${next}:${minute}`)}
            />
            <TimeColumn
              options={MINUTES}
              selected={minute}
              label={minuteLabel}
              onSelect={(next) => {
                onChange(`${hour}:${next}`);
                setOpen(false);
              }}
            />
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
